import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/firebase';

interface Activation {
  id: string;
  licenseKey: string;
  username: string;
  deviceId: string;
  deviceName: string;
  activatedAt: any;
  expiresAt: any;
  isActive: boolean;
  ipAddress: string;
  userAgent: string;
  lastSeen?: any;
  hardwareFingerprint?: string;
  osInfo?: string;
  cpuInfo?: string;
  memoryInfo?: string;
  diskInfo?: string;
  gpuInfo?: string;
  screenResolution?: string;
  revitVersion?: string; // Now can contain multiple versions separated by comma
  location?: {
    country?: string;
    city?: string;
    region?: string;
    timezone?: string;
    isp?: string;
  };
}

interface ActivationsResponse {
  success: boolean;
  data: Activation[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

export function Activations() {
  const { currentUser } = useAuth();
  const [activations, setActivations] = useState<Activation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedActivation, setSelectedActivation] = useState<Activation | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    status: 'true', // Show active by default
    expired: ''
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const fetchActivations = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/activations?page=1&limit=50`, {
        headers
      });

      if (!response.ok) {
        if (response.status === 500) {
          console.warn('Server error, but continuing with empty data');
          setActivations([]);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ActivationsResponse = await response.json();

      if (data.success) {
        setActivations(data.data || []);
      } else {
        console.warn('API returned success: false');
        setActivations([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch activations');
      console.error('Error fetching activations:', err);
      setActivations([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refreshActivations = async () => {
    setRefreshing(true);
    await fetchActivations();
  };

  const revokeActivation = async (activation: Activation) => {
    if (!confirm(`Weet je zeker dat je de activatie voor ${activation.username} op ${activation.deviceName || activation.deviceId} wilt intrekken?`)) {
      return;
    }

    setRevoking(activation.id);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/activations/${activation.id}/revoke`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        showMessage('success', 'Activatie succesvol ingetrokken');
        await fetchActivations(); // Refresh the list
      } else {
        throw new Error(data.error || 'Failed to revoke activation');
      }
    } catch (err) {
      showMessage('error', 'Fout bij intrekken van activatie: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setRevoking(null);
    }
  };

  const openModal = (activation: Activation) => {
    setSelectedActivation(activation);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedActivation(null);
  };

  const formatValue = (value: any, fallback: string = 'Niet beschikbaar') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return fallback;
    }
    return value;
  };

  const formatIPAddress = (ipAddress: string) => {
    if (!ipAddress) return 'IP niet beschikbaar';
    
    // Convert IPv6 to IPv4 if needed (client-side fallback)
    if (ipAddress.startsWith('::ffff:')) {
      return ipAddress.substring(7);
    }
    
    // Handle other IPv6 formats
    if (ipAddress.includes(':') && !ipAddress.includes('.')) {
      // Try to extract meaningful IPv4-like representation
      const segments = ipAddress.split(':');
      if (segments.length > 4) {
        return `IPv6: ${ipAddress.substring(0, 20)}...`;
      }
    }
    
    return ipAddress;
  };

  const formatRevitVersions = (revitVersion: string) => {
    if (!revitVersion || revitVersion === 'Unknown') {
      return 'Revit versie onbekend';
    }
    
    // If multiple versions, format nicely
    if (revitVersion.includes(',')) {
      const versions = revitVersion.split(',').map(v => v.trim()).filter(v => v && v !== 'Unknown');
      if (versions.length === 0) return 'Revit versie onbekend';
      if (versions.length === 1) return `Revit ${versions[0]}`;
      return `Revit ${versions.join(', ')} (${versions.length} versies)`;
    }
    
    return `Revit ${revitVersion}`;
  };

  const formatLocation = (location?: { country?: string; city?: string; region?: string; timezone?: string; isp?: string }) => {
    if (!location) return 'Locatie niet beschikbaar';
    
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.region && location.region !== location.city) parts.push(location.region);
    if (location.country) parts.push(location.country);
    
    return parts.length > 0 ? parts.join(', ') : 'Locatie niet beschikbaar';
  };

  const formatDetailedLocation = (location?: { country?: string; city?: string; region?: string; timezone?: string; isp?: string }) => {
    if (!location) return 'Locatie informatie niet beschikbaar';
    
    const details = [];
    if (location.city) details.push(`Stad: ${location.city}`);
    if (location.region && location.region !== location.city) details.push(`Regio: ${location.region}`);
    if (location.country) details.push(`Land: ${location.country}`);
    if (location.timezone) details.push(`Tijdzone: ${location.timezone}`);
    if (location.isp) details.push(`ISP: ${location.isp}`);
    
    return details.length > 0 ? details.join(', ') : 'Locatie informatie niet beschikbaar';
  };

  useEffect(() => {
    fetchActivations();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (!loading) {
        fetchActivations();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Datum niet beschikbaar';
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp formats
      if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else {
        console.warn('Unknown timestamp format:', timestamp);
        return 'Ongeldig datumformaat';
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date created from timestamp:', timestamp);
        return 'Ongeldige datum';
      }
      
      // Use toLocaleString instead of toLocaleDateString to include time
      return date.toLocaleString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp);
      return 'Fout bij datum formattering';
    }
  };

  const getStatusBadge = (activation: Activation) => {
    if (!activation.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          ⏸️ Inactief
        </span>
      );
    }
    
    try {
      const now = new Date();
      let expiresAt: Date;
      
      if (activation.expiresAt && typeof activation.expiresAt === 'object' && activation.expiresAt.seconds) {
        expiresAt = new Date(activation.expiresAt.seconds * 1000);
      } else if (activation.expiresAt && typeof activation.expiresAt === 'object' && activation.expiresAt._seconds) {
        expiresAt = new Date(activation.expiresAt._seconds * 1000);
      } else {
        expiresAt = new Date(activation.expiresAt);
      }
      
      if (isNaN(expiresAt.getTime())) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            ❓ Status onbekend
          </span>
        );
      }
      
      if (expiresAt < now) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            ⏰ Verlopen
          </span>
        );
      }
      
      const timeDiff = expiresAt.getTime() - now.getTime();
      const hoursUntilExpiry = timeDiff / (1000 * 60 * 60);
      
      if (hoursUntilExpiry < 24 && hoursUntilExpiry > 0) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            ⚠️ Verloopt Binnenkort
          </span>
        );
      }
      
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✅ Actief
        </span>
      );
    } catch (error) {
      console.error('Error determining status badge:', error);
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          ❓ Status onbekend
        </span>
      );
    }
  };

  const getTimeRemaining = (expiresAt: any) => {
    if (!expiresAt) return 'Vervaldatum onbekend';
    
    try {
      const now = new Date();
      let expireDate: Date;
      
      if (expiresAt && typeof expiresAt === 'object' && expiresAt.seconds) {
        expireDate = new Date(expiresAt.seconds * 1000);
      } else if (expiresAt && typeof expiresAt === 'object' && expiresAt._seconds) {
        expireDate = new Date(expiresAt._seconds * 1000);
      } else {
        expireDate = new Date(expiresAt);
      }
      
      if (isNaN(expireDate.getTime())) {
        return 'Ongeldige vervaldatum';
      }
      
      const timeDiff = expireDate.getTime() - now.getTime();
      
      if (timeDiff <= 0) {
        return 'Verlopen';
      }
      
      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}u`;
      }
      
      return `${hours}u ${minutes}m`;
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return 'Fout bij berekening';
    }
  };

  const parseUserAgent = (userAgent: string) => {
    if (!userAgent || userAgent.trim() === '') {
      return { browser: 'Browser niet gedetecteerd', os: 'OS niet gedetecteerd' };
    }
    
    // Simple user agent parsing
    let browser = 'Browser onbekend';
    let os = 'OS onbekend';
    
    // Browser detection
    if (userAgent.includes('Edg/')) browser = 'Microsoft Edge';
    else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) browser = 'Google Chrome';
    else if (userAgent.includes('Firefox/')) browser = 'Mozilla Firefox';
    else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) browser = 'Safari';
    else if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) browser = 'Opera';
    
    // OS detection
    if (userAgent.includes('Windows NT 10.0')) os = 'Windows 10/11';
    else if (userAgent.includes('Windows NT 6.3')) os = 'Windows 8.1';
    else if (userAgent.includes('Windows NT 6.2')) os = 'Windows 8';
    else if (userAgent.includes('Windows NT 6.1')) os = 'Windows 7';
    else if (userAgent.includes('Windows')) os = 'Windows (versie onbekend)';
    else if (userAgent.includes('Mac OS X')) {
      const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
      if (macMatch) {
        const version = macMatch[1].replace(/_/g, '.');
        os = `macOS ${version}`;
      } else {
        os = 'macOS';
      }
    }
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) {
      const androidMatch = userAgent.match(/Android (\d+\.?\d*)/);
      if (androidMatch) {
        os = `Android ${androidMatch[1]}`;
      } else {
        os = 'Android';
      }
    }
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      const iosMatch = userAgent.match(/OS (\d+_\d+)/);
      if (iosMatch) {
        const version = iosMatch[1].replace(/_/g, '.');
        os = `iOS ${version}`;
      } else {
        os = 'iOS';
      }
    }
    
    return { browser, os };
  };

  const filteredActivations = activations.filter(activation => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!activation.username.toLowerCase().includes(search) && 
          !activation.licenseKey.toLowerCase().includes(search) &&
          !activation.deviceName.toLowerCase().includes(search) &&
          !activation.deviceId.toLowerCase().includes(search) &&
          !activation.ipAddress.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (filters.status !== '' && activation.isActive.toString() !== filters.status) return false;
    if (filters.expired !== '') {
      try {
        let expiresAt: Date;
        
        if (activation.expiresAt && typeof activation.expiresAt === 'object' && activation.expiresAt.seconds) {
          expiresAt = new Date(activation.expiresAt.seconds * 1000);
        } else if (activation.expiresAt && typeof activation.expiresAt === 'object' && activation.expiresAt._seconds) {
          expiresAt = new Date(activation.expiresAt._seconds * 1000);
        } else {
          expiresAt = new Date(activation.expiresAt);
        }
        
        if (isNaN(expiresAt.getTime())) {
          return false;
        }
        
        const isExpired = expiresAt < new Date();
        if (filters.expired === 'true' && !isExpired) return false;
        if (filters.expired === 'false' && isExpired) return false;
      } catch (error) {
        console.error('Error checking expiration filter:', error);
        return false;
      }
    }
    return true;
  });

  // Calculate statistics
  const activeActivations = activations.filter(a => {
    if (!a.isActive) return false;
    try {
      let expiresAt: Date;
      if (a.expiresAt && typeof a.expiresAt === 'object' && a.expiresAt.seconds) {
        expiresAt = new Date(a.expiresAt.seconds * 1000);
      } else if (a.expiresAt && typeof a.expiresAt === 'object' && a.expiresAt._seconds) {
        expiresAt = new Date(a.expiresAt._seconds * 1000);
      } else {
        expiresAt = new Date(a.expiresAt);
      }
      return !isNaN(expiresAt.getTime()) && expiresAt > new Date();
    } catch (error) {
      return false;
    }
  });

  const expiredActivations = activations.filter(a => {
    try {
      let expiresAt: Date;
      if (a.expiresAt && typeof a.expiresAt === 'object' && a.expiresAt.seconds) {
        expiresAt = new Date(a.expiresAt.seconds * 1000);
      } else if (a.expiresAt && typeof a.expiresAt === 'object' && a.expiresAt._seconds) {
        expiresAt = new Date(a.expiresAt._seconds * 1000);
      } else {
        expiresAt = new Date(a.expiresAt);
      }
      return !isNaN(expiresAt.getTime()) && expiresAt < new Date();
    } catch (error) {
      return false;
    }
  });

  const uniqueUsers = new Set(activations.map(a => a.username)).size;

  if (loading && activations.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Activaties laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            Actieve Sessies
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Monitor actieve licentie sessies en apparaat activaties
          </p>
          <p className="text-sm text-gray-500">
            Overzicht van alle actieve en verlopen sessies met uitgebreide apparaat informatie
          </p>
        </div>

        {/* Action Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={refreshActivations}
            disabled={refreshing}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50"
          >
            <span>{refreshing ? '⏳' : '🔄'}</span>
            <span>{refreshing ? 'Vernieuwen...' : 'Vernieuwen'}</span>
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border shadow-sm ${
            message.type === 'success' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800' 
              : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
              <button
                onClick={() => setMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-semibold">Totaal Sessies</p>
                  <p className="text-3xl font-bold text-gray-900">{activations.length}</p>
                </div>
                <div className="text-4xl opacity-80">🔗</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-semibold">Actief</p>
                  <p className="text-3xl font-bold text-gray-900">{activeActivations.length}</p>
                </div>
                <div className="text-4xl opacity-80">✅</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-semibold">Verlopen</p>
                  <p className="text-3xl font-bold text-gray-900">{expiredActivations.length}</p>
                </div>
                <div className="text-4xl opacity-80">⏰</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-semibold">Unieke Gebruikers</p>
                  <p className="text-3xl font-bold text-gray-900">{uniqueUsers}</p>
                </div>
                <div className="text-4xl opacity-80">👥</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🔍</span>
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Zoeken</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Zoek op gebruikersnaam, licentie, apparaat of IP..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="">Alle Status</option>
                  <option value="true">Actief</option>
                  <option value="false">Inactief</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vervaldatum</label>
                <select
                  value={filters.expired}
                  onChange={(e) => setFilters(prev => ({ ...prev, expired: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="">Alle</option>
                  <option value="false">Geldig</option>
                  <option value="true">Verlopen</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p className="text-sm text-gray-600">
                Toont {filteredActivations.length} van {activations.length} sessies
              </p>
              <button
                onClick={() => setFilters({ search: '', status: 'true', expired: '' })}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
              >
                Filters Wissen
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex">
              <span className="text-red-400">❌</span>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Fout bij laden van activaties</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Activations Cards - Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredActivations.length === 0 ? (
            <div className="col-span-full">
              <div className="bg-white rounded-2xl shadow-2xl p-12 border border-gray-100 text-center">
                <span className="text-6xl mb-4 block">🔑</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Geen activaties gevonden</h3>
                <p className="text-gray-600">Geen actieve sessies komen overeen met je huidige filters</p>
              </div>
            </div>
          ) : (
            filteredActivations.map((activation) => {
              const { browser, os } = parseUserAgent(activation.userAgent);
              return (
                <div key={activation.id} className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02]">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-12 translate-x-12 opacity-20"></div>
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {activation.username}
                        </h3>
                        <p className="text-sm text-gray-600">{activation.deviceName || activation.deviceId}</p>
                      </div>
                      {getStatusBadge(activation)}
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Licentie Sleutel</p>
                        <p className="text-sm font-mono bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                          {activation.licenseKey.length > 20 ? `${activation.licenseKey.slice(0, 20)}...` : activation.licenseKey}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">IP Adres</p>
                          <p className="text-sm text-gray-700 font-mono">{formatIPAddress(activation.ipAddress)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Locatie</p>
                          <p className="text-sm text-gray-700">
                            {formatLocation(activation.location)}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hardware Fingerprint</p>
                        <p className="text-sm font-mono bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                          {activation.hardwareFingerprint ? 
                            (activation.hardwareFingerprint.length > 16 ? `${activation.hardwareFingerprint.slice(0, 16)}...` : activation.hardwareFingerprint) :
                            'Hardware ID niet beschikbaar'
                          }
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Browser</p>
                          <p className="text-sm text-gray-700">{browser}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">OS</p>
                          <p className="text-sm text-gray-700">{os}</p>
                        </div>
                      </div>

                      <div className="border-t pt-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Systeem Info</p>
                        <p className="text-xs text-gray-600 mb-1">OS: {formatValue(activation.osInfo, 'OS info niet beschikbaar')}</p>
                        <p className="text-xs text-gray-600 mb-1">CPU: {formatValue(activation.cpuInfo, 'CPU info niet beschikbaar')}</p>
                        <p className="text-xs text-gray-600 mb-1">RAM: {formatValue(activation.memoryInfo, 'RAM info niet beschikbaar')}</p>
                        <p className="text-xs text-gray-600">{formatRevitVersions(activation.revitVersion || '')}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Geactiveerd</p>
                        <p className="text-sm text-gray-700">{formatTimestamp(activation.activatedAt)}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Verloopt</p>
                        <p className="text-sm text-gray-700">{formatTimestamp(activation.expiresAt)}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tijd Resterend</p>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          getTimeRemaining(activation.expiresAt) === 'Verlopen' 
                            ? 'bg-red-100 text-red-800' 
                            : getTimeRemaining(activation.expiresAt).includes('u') && !getTimeRemaining(activation.expiresAt).includes('d')
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {getTimeRemaining(activation.expiresAt)}
                        </span>
                      </div>
                      
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Laatst Gezien</p>
                        <p className="text-sm text-gray-700">{formatTimestamp(activation.lastSeen)}</p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="mt-6 space-y-2">
                      <button 
                        onClick={() => openModal(activation)}
                        className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-blue-200"
                      >
                        📋 Alle Details Bekijken
                      </button>
                      <button 
                        onClick={() => revokeActivation(activation)}
                        disabled={revoking === activation.id || !activation.isActive}
                        className="w-full bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 text-red-700 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {revoking === activation.id ? '⏳ Intrekken...' : '🚫 Activatie Intrekken'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Auto-refresh indicator */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <span className="inline-flex items-center">
            <span className="animate-pulse mr-1">🔄</span>
            Automatisch vernieuwen elke 30 seconden
          </span>
        </div>
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e
        </p>
      </div>

      {/* Modal for detailed view */}
      {showModal && selectedActivation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <span className="mr-3">📋</span>
                    Activatie Details
                  </h2>
                  <p className="text-gray-600 mt-1">Volledige informatie voor {selectedActivation.username}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Status Badge */}
              <div className="mb-6">
                {getStatusBadge(selectedActivation)}
              </div>

              {/* Modal Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">👤</span>
                    Basis Informatie
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Gebruikersnaam</p>
                      <p className="text-gray-900 font-medium">{formatValue(selectedActivation.username)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Apparaat Naam</p>
                      <p className="text-gray-900">{formatValue(selectedActivation.deviceName, 'Apparaat naam niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Apparaat ID</p>
                      <p className="text-gray-900 font-mono text-sm break-all">{formatValue(selectedActivation.deviceId)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Licentie Sleutel</p>
                      <p className="text-gray-900 font-mono text-sm break-all bg-gray-100 p-2 rounded">{formatValue(selectedActivation.licenseKey)}</p>
                    </div>
                  </div>
                </div>

                {/* Network Information */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🌐</span>
                    Netwerk Informatie
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">IP Adres</p>
                      <p className="text-gray-900 font-mono">{formatIPAddress(selectedActivation.ipAddress)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Locatie Details</p>
                      <p className="text-gray-900 text-sm">{formatDetailedLocation(selectedActivation.location)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">User Agent</p>
                      <p className="text-gray-900 text-sm break-all bg-gray-100 p-2 rounded">{formatValue(selectedActivation.userAgent, 'User agent niet beschikbaar')}</p>
                    </div>
                  </div>
                </div>

                {/* Hardware Information */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🖥️</span>
                    Hardware Informatie
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Hardware Fingerprint</p>
                      <p className="text-gray-900 font-mono text-sm break-all bg-gray-100 p-2 rounded">{formatValue(selectedActivation.hardwareFingerprint, 'Hardware fingerprint niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Besturingssysteem</p>
                      <p className="text-gray-900">{formatValue(selectedActivation.osInfo, 'OS informatie niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Processor</p>
                      <p className="text-gray-900">{formatValue(selectedActivation.cpuInfo, 'CPU informatie niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Geheugen</p>
                      <p className="text-gray-900">{formatValue(selectedActivation.memoryInfo, 'Geheugen informatie niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Schijfruimte</p>
                      <p className="text-gray-900 text-sm">{formatValue(selectedActivation.diskInfo, 'Schijf informatie niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Grafische Kaart</p>
                      <p className="text-gray-900 text-sm">{formatValue(selectedActivation.gpuInfo, 'GPU informatie niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Schermresolutie</p>
                      <p className="text-gray-900">{formatValue(selectedActivation.screenResolution, 'Schermresolutie niet beschikbaar')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Revit Versie(s)</p>
                      <p className="text-gray-900">{formatRevitVersions(selectedActivation.revitVersion || '')}</p>
                    </div>
                  </div>
                </div>

                {/* Browser & System Detection */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 border border-orange-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🔍</span>
                    Gedetecteerde Informatie
                  </h3>
                  <div className="space-y-3">
                    {(() => {
                      const { browser, os } = parseUserAgent(selectedActivation.userAgent);
                      return (
                        <>
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Browser</p>
                            <p className="text-gray-900">{browser}</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Besturingssysteem</p>
                            <p className="text-gray-900">{os}</p>
                          </div>
                        </>
                      );
                    })()}
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Status</p>
                      <p className="text-gray-900">{selectedActivation.isActive ? 'Actief' : 'Inactief'}</p>
                    </div>
                  </div>
                </div>

                {/* Timing Information */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">⏰</span>
                    Timing Informatie
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Geactiveerd op</p>
                      <p className="text-gray-900">{formatTimestamp(selectedActivation.activatedAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Verloopt op</p>
                      <p className="text-gray-900">{formatTimestamp(selectedActivation.expiresAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Laatst gezien</p>
                      <p className="text-gray-900">{formatTimestamp(selectedActivation.lastSeen)}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700">Tijd resterend</p>
                    <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium mt-1 ${
                      getTimeRemaining(selectedActivation.expiresAt) === 'Verlopen' 
                        ? 'bg-red-100 text-red-800' 
                        : getTimeRemaining(selectedActivation.expiresAt).includes('u') && !getTimeRemaining(selectedActivation.expiresAt).includes('d')
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {getTimeRemaining(selectedActivation.expiresAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={closeModal}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-200"
                >
                  Sluiten
                </button>
                <button
                  onClick={() => {
                    revokeActivation(selectedActivation);
                    closeModal();
                  }}
                  disabled={revoking === selectedActivation.id || !selectedActivation.isActive}
                  className="px-6 py-2 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {revoking === selectedActivation.id ? '⏳ Intrekken...' : '🚫 Activatie Intrekken'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Activations; 